import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2, ArrowRight, Share2 } from "lucide-react";
import confetti from "canvas-confetti";
import { verifyCertificate } from "../lib/api";
import { toast } from "sonner";

export default function VerifyPage() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, cert: null, error: false });

  useEffect(() => {
    let alive = true;
    verifyCertificate(certId)
      .then((cert) => alive && setState({ loading: false, cert, error: false }))
      .catch(() => alive && setState({ loading: false, cert: null, error: true }));
    return () => {
      alive = false;
    };
  }, [certId]);

  useEffect(() => {
    if (!state.cert) return;
    const t = setTimeout(() => {
      confetti({
        particleCount: 90,
        spread: 72,
        origin: { y: 0.35 },
        colors: ["#0d9488", "#10b981", "#f59e0b", "#38bdf8"],
      });
    }, 200);
    return () => clearTimeout(t);
  }, [state.cert]);

  const issued = state.cert?.issued_at
    ? new Date(state.cert.issued_at).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const shareCertificate = async () => {
    const who = state.cert?.name ? `${state.cert.name} is` : "I'm";
    const text = `${who} a verified ${state.cert?.program || "Healthy-Living Explorer"} on AskMyDoctor AI! 🌿`;
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Verified Certificate", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Certificate link copied — paste it anywhere to share!");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-16" data-testid="verify-page">
      <div className="w-full max-w-md">
        {state.loading ? (
          <div className="flex items-center justify-center gap-2 text-slate-500" data-testid="verify-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Checking certificate…
          </div>
        ) : state.cert ? (
          <div
            className="rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-8 text-center shadow-sm"
            data-testid="verify-valid"
          >
            <div className="mx-auto mb-4 grid place-items-center w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600">
              <ShieldCheck className="w-9 h-9" />
            </div>
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-700">
              Verified Certificate
            </p>
            <h1 className="mt-2 font-heading font-extrabold text-3xl text-slate-900">
              {state.cert.program}
            </h1>
            <p className="mt-2 text-lg text-slate-600" data-testid="verify-name">
              {state.cert.name ? `Awarded to ${state.cert.name}` : "Awarded to a Healthy-Living Explorer"}
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 font-semibold text-xs px-3 py-1 rounded-full" data-testid="verify-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> Verified by AskMyDoctor AI
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 border-t border-emerald-200/70 pt-4 text-xs font-semibold text-slate-500">
              <span className="font-mono tracking-wider text-slate-600" data-testid="verify-id">
                ID · {state.cert.cert_id}
              </span>
              {issued && <span>Issued {issued}</span>}
            </div>
            <p className="mt-4 text-sm text-slate-500">
              This certificate is genuine and was issued by AskMyDoctor AI.
            </p>
            <button
              onClick={shareCertificate}
              data-testid="verify-share"
              className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base px-6 py-2.5 rounded-xl transition-transform active:scale-95"
            >
              <Share2 className="w-5 h-5" /> Share this certificate
            </button>
          </div>
        ) : (
          <div
            className="rounded-3xl border-2 border-red-200 bg-red-50/50 p-8 text-center"
            data-testid="verify-invalid"
          >
            <div className="mx-auto mb-4 grid place-items-center w-16 h-16 rounded-2xl bg-red-100 text-red-600">
              <ShieldX className="w-9 h-9" />
            </div>
            <h1 className="font-heading font-extrabold text-2xl text-slate-900">
              Certificate not found
            </h1>
            <p className="mt-2 text-base text-slate-600">
              We couldn't verify a certificate with the ID{" "}
              <span className="font-mono font-semibold text-slate-700">{certId}</span>.
            </p>
          </div>
        )}
        <div className="mt-8 text-center">
          <Link
            to="/wellness"
            data-testid="verify-explore-link"
            className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold text-base hover:text-emerald-700"
          >
            Explore the Wellness program <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
